import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'input',
    'dropzone',
    'fileList',
    'thumbnail',
    'preview',
    'emptyState',
    'inline',
    'inlineIcon',
    'inlineName',
    'inlineExt',
  ];

  static values = {
    acceptMode: String,
    accept: String,
    variant: String, // "dropzone" (default) | "inline"
    modalId: String,
  };

  connect() {
    if (this.hasInputTarget) {
      const mode = (this.acceptModeValue || 'all').toLowerCase();
      const provided =
        this.hasAcceptValue && this.acceptValue.trim().length > 0 ? this.acceptValue.trim() : null;

      const fallbackAccept =
        mode === 'image'
          ? 'image/*'
          : '.png,.jpg,.jpeg,.heic,.heif,.bmp,.gif,.tiff,.tif,.mp4,.mov,.doc,.docx,.pdf,.odt,.pages,.txt,.xlsx,.xls,.xlsm,.csv,.numbers,.ppt,.pptx,.key,.msg,.vcf';

      this.inputTarget.setAttribute('accept', provided || fallbackAccept);
    }

    this._resetUI();
  }

  // ------- UI helpers -------
  _resetUI() {
    const isInline = (this.variantValue || 'dropzone') === 'inline';

    if (isInline) {
      if (this.hasInlineTarget) this._hide(this.inlineTarget);
      if (this.hasDropzoneTarget) this._hide(this.dropzoneTarget);
      if (this.hasPreviewTarget) this._hide(this.previewTarget);
      if (this.hasFileListTarget) this._hide(this.fileListTarget);
      if (this.hasThumbnailTarget) this.thumbnailTarget.src = '#';
      if (this.hasFileListTarget) this.fileListTarget.innerHTML = '';
    } else {
      if (this.hasDropzoneTarget) this._show(this.dropzoneTarget);
      if (this.hasEmptyStateTarget) this._show(this.emptyStateTarget);
      if (this.hasPreviewTarget) this._hide(this.previewTarget);
      if (this.hasFileListTarget) {
        this._hide(this.fileListTarget);
        this.fileListTarget.innerHTML = '';
      }
      if (this.hasThumbnailTarget) this.thumbnailTarget.src = '#';
      if (this.hasInlineTarget) this._hide(this.inlineTarget);
    }
  }

  _show(el) {
    if (el) {
      el.classList.remove('hidden');
      el.classList.add('flex');
    }
  }
  _hide(el) {
    if (el) {
      el.classList.remove('flex');
      el.classList.add('hidden');
    }
  }

  // ------- D&D only -------
  highlight(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget)
      this.dropzoneTarget.classList.add('border-black-300', 'bg-black-50');
  }
  unhighlight(event) {
    event.preventDefault();
    if (this.hasDropzoneTarget)
      this.dropzoneTarget.classList.remove('border-black-300', 'bg-black-50');
  }
  handleDrop(event) {
    event.preventDefault();
    this.unhighlight(event);
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    if (this.hasInputTarget) this.inputTarget.files = event.dataTransfer.files;
    this._handleFiles(files);
  }

  _handleFiles(files) {
  const valid = files.filter((f) => this._isAllowed(f));
  if (!valid.length) return;

  const mode = (this.acceptModeValue || 'all').toLowerCase();
  const isInline = (this.variantValue || 'dropzone') === 'inline';

  if (isInline) {
    this._renderInline(valid[0]);
  } else if (mode === 'image') {
    const first = valid[0];
    if (!first.type.startsWith('image/')) return;
    this._previewImage(first);
  } else {
    this._renderBatchProgress(valid);
  }

  this._emitConfirm(valid);

  this._requestClose(true);
}


    handleChoose(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    this._handleFiles(files);
    }

    triggerReplace(event) {
        if (event?.target?.closest?.('label')) return;
        if (this.hasInputTarget) this.inputTarget.click();
    }

    _handleFile(file) {
    if (!this._isAllowed(file)) return;

    const isInline = (this.variantValue || 'dropzone') === 'inline';
    if (isInline) {
      this._renderInline(file);
      return;
    }

    const mode = (this.acceptModeValue || 'all').toLowerCase();
    if (mode === 'image') {
      if (!file.type.startsWith('image/')) return;
      this._previewImage(file);
    } else {
      this._renderFileCardAndRead(file);
    }
  }

  _isAllowed(file) {
    const mode = (this.acceptModeValue || 'all').toLowerCase();
    const provided =
      this.hasAcceptValue && this.acceptValue.trim().length > 0 ? this.acceptValue.trim() : null;

    if (provided) {
      const parts = provided.split(',').map((s) => s.trim().toLowerCase());
      if (parts.includes('image/*') && file.type.toLowerCase().startsWith('image/')) return true;
      if (parts.includes('video/*') && file.type.toLowerCase().startsWith('video/')) return true;

      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (parts.some((p) => p.startsWith('.') && p.slice(1) === ext)) return true;

      if (parts.includes(file.type.toLowerCase())) return true;

      return false;
    }

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const type = file.type.toLowerCase();

    // Business-approved file types
    const ALLOWED = {
      image: {
        exts: new Set(['png', 'jpg', 'jpeg', 'heic', 'heif', 'bmp', 'gif', 'tiff', 'tif']),
        mimes: new Set(['image/png', 'image/jpeg', 'image/heic', 'image/heif', 'image/bmp', 'image/gif', 'image/tiff']),
      },
      video: {
        exts: new Set(['mp4', 'mov']),
        mimes: new Set(['video/mp4', 'video/quicktime']),
      },
      document: {
        exts: new Set(['doc', 'docx', 'pdf', 'odt', 'pages', 'txt']),
        mimes: new Set([
          'application/pdf',
          'application/x-pdf',
          'text/plain',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.oasis.opendocument.text',
          'application/vnd.apple.pages',
        ]),
      },
      spreadsheet: {
        exts: new Set(['xlsx', 'xls', 'xlsm', 'csv', 'numbers']),
        mimes: new Set([
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'application/vnd.ms-excel.sheet.macroenabled.12',
          'text/csv',
          'application/vnd.apple.numbers',
        ]),
      },
      presentation: {
        exts: new Set(['ppt', 'pptx', 'key']),
        mimes: new Set([
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.apple.keynote',
        ]),
      },
      other: {
        exts: new Set(['msg', 'vcf']),
        mimes: new Set(['application/vnd.ms-outlook', 'text/vcard', 'text/x-vcard']),
      },
    };

    const matches = ({ exts, mimes }) => exts.has(ext) || mimes.has(type);
    const isImage = matches(ALLOWED.image);
    const isAny = Object.values(ALLOWED).some(matches);

    if (mode === 'image') return isImage;
    if (mode === 'document' || mode === 'all') return isAny;

    return false;
  }

  _previewImage(file) {
    const reader = new FileReader();
    reader.onload = () => {
      if (this.hasThumbnailTarget) this.thumbnailTarget.src = reader.result;
      if (this.hasDropzoneTarget) this._hide(this.dropzoneTarget);
      if (this.hasFileListTarget) this._hide(this.fileListTarget);
      if (this.hasPreviewTarget) this._show(this.previewTarget);
    };
    reader.readAsDataURL(file);
  }

  _renderFileCardAndRead(file) {
    if (this.hasPreviewTarget) this._hide(this.previewTarget);
    if (this.hasDropzoneTarget) this._show(this.dropzoneTarget);
    if (this.hasEmptyStateTarget) this._hide(this.emptyStateTarget);
    if (this.hasFileListTarget) this._show(this.fileListTarget);
    if (this.hasFileListTarget) this.fileListTarget.innerHTML = '';

    const rawExt = (file.name.split('.').pop() || '').trim();
    const extCap = rawExt ? rawExt.charAt(0).toUpperCase() + rawExt.slice(1).toLowerCase() : 'File';
    const totalBytes = file.size;

    const li = document.createElement('li');
    li.className = '!p-20 !bg-white !w-full';
    li.innerHTML = `
      <div class="w-full flex items-center justify-center mb-16">
        <div class="relative inline-block">
          <div class="flex items-center justify-center">
            <svg width="38" height="40" viewBox="0 0 38 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M10 0.5H24.793L37.5 13.207V36C37.5 37.933 35.933 39.5 34 39.5H10C8.067 39.5 6.5 37.933 6.5 36V4C6.5 2.067 8.067 0.5 10 0.5Z" fill="white" stroke="#E9E6E7"/>
              <path d="M25 8V0L38 13H30C27.2386 13 25 10.7614 25 8Z" fill="#E9E6E7"/>
            </svg>
          </div>
          <span class="absolute -left-2 -bottom-2 px-2 py-4 bg-gray-100 text-black-1000 text-[10px] font-semibold uppercase rounded">
            ${extCap}
          </span>
        </div>
      </div>

      <div class="flex items-center justify-between mb-10">
        <div class="text-paragraph-s font-medium text-black-1000">Completed</div>
        <div class="text-paragraph-s font-medium text-black-1000" data-role="percent">0%</div>
      </div>

      <div class="w-full h-[6px] rounded-full bg-black-900/10 overflow-hidden">
        <div class="h-[6px] rounded-full bg-black-900/90" style="width:0%" data-role="bar"></div>
      </div>

      <div class="mt-8 text-paragraph-s font-regular text-black-400" data-role="sizes">
        0 of ${this._formatBytes(totalBytes)}
      </div>
    `;

    if (this.hasFileListTarget) this.fileListTarget.appendChild(li);

    const bar = li.querySelector('[data-role="bar"]');
    const percentEl = li.querySelector('[data-role="percent"]');
    const sizesEl = li.querySelector('[data-role="sizes"]');

    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (!e.lengthComputable) return;
      const loaded = e.loaded;
      const pct = Math.min(100, Math.round((loaded / totalBytes) * 100));
      if (bar) bar.style.width = `${pct}%`;
      if (percentEl) percentEl.textContent = `${pct}%`;
      if (sizesEl)
        sizesEl.textContent = `${this._formatBytes(loaded)} of ${this._formatBytes(totalBytes)}`;
    };
    reader.onloadend = () => {
      if (bar) bar.style.width = '100%';
      if (percentEl) percentEl.textContent = '100%';
      if (sizesEl)
        sizesEl.textContent = `${this._formatBytes(totalBytes)} of ${this._formatBytes(totalBytes)}`;
    };
    reader.readAsArrayBuffer(file);
  }

  _renderBatchProgress(files) {
  if (this.hasPreviewTarget) this._hide(this.previewTarget);
  if (this.hasDropzoneTarget) this._show(this.dropzoneTarget);
  if (this.hasEmptyStateTarget) this._hide(this.emptyStateTarget);
  if (this.hasFileListTarget) {
    this._show(this.fileListTarget);
    this.fileListTarget.innerHTML = '';
  }

  const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);
  const extraCount = files.length > 1 ? files.length - 1 : 0;

  const firstExtRaw = (files[0].name.split('.').pop() || '').trim();
  const badge = extraCount > 0 ? `+${extraCount}` :
                (firstExtRaw ? (firstExtRaw[0].toUpperCase() + firstExtRaw.slice(1).toLowerCase()) : 'File');

  const li = document.createElement('li');
  li.className = '!p-20 !bg-white !w-full';
  li.innerHTML = `
    <div class="w-full flex items-center justify-center mb-16">
      <div class="relative inline-block">
        <div class="flex items-center justify-center">
          <svg width="38" height="40" viewBox="0 0 38 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M10 0.5H24.793L37.5 13.207V36C37.5 37.933 35.933 39.5 34 39.5H10C8.067 39.5 6.5 37.933 6.5 36V4C6.5 2.067 8.067 0.5 10 0.5Z" fill="white" stroke="#E9E6E7"/>
            <path d="M25 8V0L38 13H30C27.2386 13 25 10.7614 25 8Z" fill="#E9E6E7"/>
          </svg>
        </div>
        <span class="absolute -left-2 -bottom-2 px-2 py-4 bg-gray-100 text-black-1000 text-[10px] font-semibold uppercase rounded">
          ${badge}
        </span>
      </div>
    </div>

    <div class="flex items-center justify-between mb-10">
      <div class="text-paragraph-s font-medium text-black-1000">Completed</div>
      <div class="text-paragraph-s font-medium text-black-1000" data-role="percent">0%</div>
    </div>

    <div class="w-full h-[6px] rounded-full bg-black-900/10 overflow-hidden">
      <div class="h-[6px] rounded-full bg-black-900/90" style="width:0%" data-role="bar"></div>
    </div>

    <div class="mt-8 text-paragraph-s font-regular text-black-400" data-role="sizes">
      0 of ${this._formatBytes(totalBytes)}
    </div>
  `;

  if (this.hasFileListTarget) this.fileListTarget.appendChild(li);

  const bar = li.querySelector('[data-role="bar"]');
  const percentEl = li.querySelector('[data-role="percent"]');
  const sizesEl = li.querySelector('[data-role="sizes"]');

  let loadedTotal = 0;
  const perFileLoaded = new Map();

  const updateUI = () => {
    const pct = totalBytes ? Math.min(100, Math.round((loadedTotal / totalBytes) * 100)) : 100;
    if (bar) bar.style.width = `${pct}%`;
    if (percentEl) percentEl.textContent = `${pct}%`;
    if (sizesEl) sizesEl.textContent = `${this._formatBytes(loadedTotal)} of ${this._formatBytes(totalBytes)}`;
  };

  files.forEach((file, idx) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (!e.lengthComputable) return;
      perFileLoaded.set(idx, e.loaded);
      loadedTotal = Array.from(perFileLoaded.values()).reduce((a, b) => a + b, 0);
      updateUI();
    };
    reader.onloadend = () => {
      perFileLoaded.set(idx, file.size || 0);
      loadedTotal = Array.from(perFileLoaded.values()).reduce((a, b) => a + b, 0);
      updateUI();
    };
    reader.readAsArrayBuffer(file);
  });
}


  _renderInline(file) {
    const name = file.name || 'file';
    const ext = (name.split('.').pop() || '').toLowerCase();
    const base = name.replace(new RegExp(`\\.${ext}$`, 'i'), '');

    if (this.hasInlineNameTarget) this.inlineNameTarget.textContent = base;
    if (this.hasInlineExtTarget) this.inlineExtTarget.textContent = ext ? `.${ext}` : '';

    const setGenericIcon = () => {
      if (!this.hasInlineIconTarget) return;
      this.inlineIconTarget.innerHTML = `
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M5 1h7l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2Z" stroke="#9CA3AF" fill="white"/>
          <path d="M12 1v5h5" fill="#E5E7EB"/>
        </svg>
      `;
    };

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        if (!this.hasInlineIconTarget) return;
        this.inlineIconTarget.innerHTML = `<img src="${reader.result}" class="w-[10px] h-[10px] object-cover rounded" alt="">`;
      };
      reader.onerror = setGenericIcon;
      reader.readAsDataURL(file);
    } else {
      setGenericIcon();
    }

    if (this.hasInlineTarget) this._show(this.inlineTarget);
  }

  _formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '0B';
    const k = 1024;
    if (Math.abs(bytes) < k) return `${bytes}B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let i = -1;
    do {
      bytes /= k;
      ++i;
    } while (Math.abs(bytes) >= k && i < units.length - 1);
    return `${bytes.toFixed(bytes >= 100 ? 0 : bytes >= 10 ? 1 : 2)}${units[i]}`;
  }

  clearInline() {
    if (this.hasInputTarget) {
      this.inputTarget.value = '';
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.hasInlineNameTarget) this.inlineNameTarget.textContent = '';
    if (this.hasInlineExtTarget) this.inlineExtTarget.textContent = '';
    if (this.hasInlineTarget) this._hide(this.inlineTarget);

    if (this.hasPreviewTarget) this._hide(this.previewTarget);
    if (this.hasFileListTarget) {
      this.fileListTarget.innerHTML = '';
      this._hide(this.fileListTarget);
    }

    this.element.dispatchEvent(new CustomEvent('file-upload-modal:cleared', { bubbles: true }));
  }

  _emitConfirm(filesArray) {
    const fieldName = this.inputTarget?.name;
    this.element.dispatchEvent(new CustomEvent('file-upload-modal:confirm', {
      bubbles: true,
      detail: { files: filesArray, fieldName }
    }));
  }

   _requestClose(resetAfter = false) {
  const id = this.modalIdValue || this.inputTarget?.closest('[data-modal-id]')?.dataset.modalId;

  if (id) {
    this.element.dispatchEvent(new CustomEvent('local-modal-manager:close', { bubbles: true, detail: { id } }));
  } else {
    const modalRoot = this.element.closest('[data-controller~="modal"]') || document;
    modalRoot.querySelector('[data-action*="modal#cancel"],[data-action*="modal#confirm"]')?.click();
  }

  if (resetAfter) {
    setTimeout(() => {
      if (this.hasInputTarget) this.inputTarget.value = '';
      this._resetUI();
    }, 0);
  }
}

}
